"use client";
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const services = [
    {
        num: '01',
        title: 'Social Media Strategy',
        desc: 'Strong social media starts with strategy, not posting schedules. We develop audience-focused content plans, positioning frameworks, platform strategies, and communication systems aligned with your business goals.',
        metrics: ['+40% Brand Recall', '2× Audience Growth', 'Platform-fit Content'],
    },
    {
        num: '02',
        title: 'Content Strategy & Planning',
        desc: 'Every piece of content should serve a purpose. We create content pillars designed to educate, engage, build authority, and move audiences closer to action.',
        metrics: ['3–5% CTR', '2–4× Engagement', 'Authority Content'],
    },
    {
        num: '03',
        title: 'Creative Development',
        desc: 'From static creatives and carousels to short-form videos and campaigns, we develop content that reflects your brand while earning attention in crowded feeds.',
        metrics: ['+100% Reach', 'Scroll-stopping Design', 'On-brand Always'],
    },
    {
        num: '04',
        title: 'Community Management',
        desc: 'Social media is a conversation, not a broadcast. We help brands engage with audiences, strengthen relationships, and build credibility through meaningful interactions.',
        metrics: ['+30% Retention', 'Response Rate ↑', 'Trust Building'],
    },
    {
        num: '05',
        title: 'Social Media Advertising',
        desc: 'We combine organic content with Meta Ads and paid social campaigns to expand reach, generate leads, and accelerate growth.',
        metrics: ['4–6× ROAS', '-40% CPL', '+25% Conversions'],
    },
    {
        num: '06',
        title: 'Performance Tracking & Insights',
        desc: 'Understanding behaviour is just as important as measuring reach. We analyse engagement patterns, audience response, content performance, and conversion signals to continuously improve results.',
        metrics: ['Data-driven Decisions', 'Weekly Reporting', 'Conversion Signals'],
    },
];

function ServiceRow({ service, index }: { service: typeof services[0]; index: number }) {
    const [open, setOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.07 }}
        >
            <button
                onClick={() => setOpen(!open)}
                className="w-full text-left group"
            >
                <div
                    className="flex items-center gap-6 py-6 transition-all duration-200"
                    style={{
                        borderBottom: open ? '1px solid rgba(206,255,26,0.20)' : '1px solid rgba(206,255,26,0.08)',
                        background: open ? 'rgba(206,255,26,0.025)' : 'transparent',
                        paddingLeft: 8,
                        paddingRight: 8,
                        borderRadius: open ? '12px 12px 0 0' : 0,
                    }}
                >
                    {/* Number */}
                    <span
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 700,
                            fontSize: 13,
                            color: open ? '#ceff1a' : 'rgba(206,255,26,0.30)',
                            letterSpacing: '0.1em',
                            minWidth: 28,
                            transition: 'color 0.2s ease',
                        }}
                    >
                        {service.num}
                    </span>

                    {/* Line */}
                    <div style={{ flex: '0 0 40px', height: 1, background: open ? 'rgba(206,255,26,0.4)' : 'rgba(206,255,26,0.12)', transition: 'background 0.2s ease' }} />

                    {/* Title */}
                    <span
                        className="flex-1"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 700,
                            fontSize: 'clamp(18px, 1.8vw, 24px)',
                            color: open ? '#fbfdfd' : 'rgba(251,253,253,0.70)',
                            letterSpacing: '0.01em',
                            transition: 'color 0.2s ease',
                        }}
                    >
                        {service.title}
                    </span>

                    {/* Metric pills — visible on row when closed */}
                    <div className="hidden md:flex gap-2 items-center">
                        {!open && service.metrics.slice(0, 2).map((m, i) => (
                            <span
                                key={i}
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    color: 'rgba(206,255,26,0.50)',
                                    background: 'rgba(206,255,26,0.06)',
                                    border: '1px solid rgba(206,255,26,0.12)',
                                    borderRadius: 99,
                                    padding: '3px 10px',
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {m}
                            </span>
                        ))}
                    </div>

                    {/* Toggle icon */}
                    <motion.div
                        animate={{ rotate: open ? 45 : 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            width: 32,
                            height: 32,
                            border: `1px solid ${open ? 'rgba(206,255,26,0.35)' : 'rgba(206,255,26,0.15)'}`,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            background: open ? 'rgba(206,255,26,0.08)' : 'transparent',
                            transition: 'border-color 0.2s ease, background 0.2s ease',
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 1v10M1 6h10" stroke={open ? '#ceff1a' : 'rgba(206,255,26,0.45)'} strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                    </motion.div>
                </div>
            </button>

            {/* Expanded content */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div
                            className="flex flex-col md:flex-row gap-8 p-6 md:p-8"
                            style={{
                                background: 'rgba(206,255,26,0.025)',
                                border: '1px solid rgba(206,255,26,0.20)',
                                borderTop: 'none',
                                borderRadius: '0 0 12px 12px',
                            }}
                        >
                            <p
                                className="flex-1 text-sm md:text-base leading-relaxed"
                                style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.65)', letterSpacing: '0.025em' }}
                            >
                                {service.desc}
                            </p>
                            <div className="flex flex-row md:flex-col gap-2 flex-wrap md:flex-nowrap md:min-w-[200px]">
                                {service.metrics.map((m, i) => (
                                    <span
                                        key={i}
                                        className="self-start"
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                            color: '#ceff1a',
                                            background: 'rgba(206,255,26,0.08)',
                                            border: '1px solid rgba(206,255,26,0.22)',
                                            borderRadius: 99,
                                            padding: '5px 14px',
                                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function SocialPlatforms() {
    return (
        <section className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>

            <div className="max-w-[1400px] mx-auto flex flex-col gap-12 relative z-10">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            What We Offer
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(34px, 4.5vw, 62px)', lineHeight: 1.0, letterSpacing: '-0.02em' }}
                        >
                            <span style={{ color: '#ceff1a' }}>Our Social Media</span>
                            <br />
                            <span style={{
                                background: 'linear-gradient(180deg, #fbfdfd 0%, rgba(251,253,253,0.50) 100%)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                            }}>Marketing Services</span>
                        </motion.h2>
                    </div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                        className="max-w-xs text-sm leading-relaxed"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(251,253,253,0.35)', letterSpacing: '0.03em' }}
                    >
                        Not just a content machine — a trust-building system with measurable outcomes at every step. Click any service to explore.
                    </motion.p>
                </div>

                {/* Accordion service rows */}
                <div className="flex flex-col">
                    {services.map((s, i) => (
                        <ServiceRow key={i} service={s} index={i} />
                    ))}
                </div>

            </div>
        </section>
    );
}
